import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaStar, FaRegClock, FaRegLightbulb } from 'react-icons/fa';
import ConsultationForm from '../components/ConsultationForm';
import { servicesData as services } from '../data/servicesData';
import '../styles/Services.css';

// Animation variants
const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.1
        }
    }
};

const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            stiffness: 100
        }
    }
};

const hoverEffect = {
    scale: 1.02,
    y: -10,
    boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
    transition: {
        type: 'spring',
        stiffness: 300
    }
};

const tapEffect = {
    scale: 0.98
};

const Services = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    // List of high-quality interior design images from Unsplash
    // const interiorImages = [
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    //     'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1374&q=80',
    // ];

    // // Function to get a random image URL
    // const getRandomImage = (index) => {
    //     return interiorImages[index % interiorImages.length];
    // };

    // Decorate services with images and use as source of truth
    const servicesWithImages = services;
    const [visibleServices, setVisibleServices] = useState(servicesWithImages);
    const [showQuoteForm, setShowQuoteForm] = useState(false);
    const [selectedService, setSelectedService] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const [flipped, setFlipped] = useState(new Set());
    const containerRef = useRef(null);

    const handleGetStarted = (serviceTitle) => {
        setSelectedService(serviceTitle);
        setShowQuoteForm(true);
    };

    // Detect mobile viewport to adjust interactions
    useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const update = () => setIsMobile(mq.matches);
        update();
        mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
        return () => {
            mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
        };
    }, []);

    const toggleFlip = (id, event) => {
        if (!isMobile) return;
        // Ignore taps on interactive elements inside the card
        if (event && (event.target.closest('button') || event.target.closest('a'))) return;
        setFlipped(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // moved up: interiorImages/getRandomImage above to avoid ReferenceError

    // Filter services by category
    useEffect(() => {
        setVisibleServices(
            activeCategory === 'all'
                ? [...servicesWithImages]
                : servicesWithImages.filter(service => service.category === activeCategory)
        );
    }, [activeCategory]);

    // Get unique categories
    const categories = ['all', ...new Set(servicesWithImages.map(service => service.category))];

    return (
        <div className="services-container">
            <div className="services-header">
                <motion.h2
                    className="section-title"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    Our Premium <span className="highlight">Services</span>
                </motion.h2>
                <p className="section-subtitle" style={{ marginTop: '0.4rem', color: 'var(--text-secondary)' }}>
                    Balanced by Vastu and personalized to your zodiac—light-touch guidance to enhance comfort and flow.
                </p>

                <motion.div
                    className="category-filters"
                    variants={container}
                    initial="hidden"
                    animate="show"
                >
                    {categories.map((category, index) => (
                        <motion.button
                            key={category}
                            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
                            onClick={() => setActiveCategory(category)}
                            variants={item}
                            whileHover={{ scale: 1.05 }}
                            whileTap={tapEffect}
                        >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                        </motion.button>
                    ))}
                </motion.div>
            </div>

            <motion.div
                className="services-grid"
                ref={containerRef}
                variants={container}
                initial="hidden"
                animate="show"
            >
                {visibleServices.map((service, index) => (
                    <motion.div
                        key={service.id}
                        className={`service-card ${isMobile && flipped.has(service.id) ? 'flipped' : ''}`}
                        variants={item}
                        whileHover={isMobile ? {} : hoverEffect}
                        whileTap={tapEffect}
                        onClick={(e) => toggleFlip(service.id, e)}
                    >
                        <div className="card-inner">
                            <div className="card-front">
                                <div className="card-image">
                                    <img
                                        src={service.imageUrl}
                                        alt={service.title}
                                        loading="lazy"
                                        style={{
                                            width: '100%',
                                            // height: '200px',
                                            objectFit: 'cover',
                                            objectPosition: 'center'
                                        }}
                                    />

                                </div>
                                <div className="card-content">
                                    <div className="card-header">
                                        <h3 className="card-title">{service.title}</h3>
                                        <p className="card-price">From {service.startingPrice || '999'}</p>
                                    </div>
                                    <p className="card-desc">{service.description}</p>
                                    <div className="card-meta">
                                        <span className="meta-item">
                                            <FaRegClock className="meta-icon" />
                                            {service.duration || '2-4 Weeks'}
                                        </span>
                                        <span className="meta-item">
                                            <FaRegLightbulb className="meta-icon" />
                                            {service.style || 'Modern'}
                                        </span>
                                    </div>
                                    <p style={{ marginTop: '0.35rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                                        Light-touch Vastu & zodiac suggestions available on request.
                                    </p>
                                </div>
                            </div>
                            <div className="card-back">
                                <h4>What's Included</h4>
                                <ul className="feature-list">
                                    {service.features.map((feature, idx) => (
                                        <li key={idx} className="feature-item">
                                            <span className="check-icon">✓</span>
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className="cta-button1"
                                    onClick={() => handleGetStarted(service.title)}
                                >
                                    Get Started
                                    <FaArrowRight className="arrow-icon" />
                                </button>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </motion.div>

            <motion.section
                className="cta-section"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
            >
                <div className="cta-content">
                    <h3>Ready to Transform Your Space?</h3>
                    <p>Let's create something amazing together. Our design experts are ready to bring your vision to life.</p>
                    <div className="cta-buttons">
                        <button
                            className="primary-btn"
                            onClick={() => {
                                setSelectedService('');
                                setShowQuoteForm(true);
                            }}
                        >
                            Get a Free Quote
                        </button>
                        <Link to="/portfolio" className="primary-btn">
                            View Our Work
                        </Link>
                    </div>
                </div>
            </motion.section>

            {showQuoteForm && (
                <ConsultationForm
                    isOpen={showQuoteForm}
                    onClose={() => setShowQuoteForm(false)}
                    type="quote"
                    initialService={selectedService}
                />
            )}
        </div>
    );
};

export default Services;
